import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Divider,
} from '@mui/material';
import {
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { usuarioService, Usuario } from '../../services/usuario';

const UsuarioVisualizar: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  // Buscar usuário por ID
  const { data: usuario, isLoading, error } = useQuery({
    queryKey: ['usuario', id],
    queryFn: () => usuarioService.getById(id!),
    enabled: !!id,
  });

  const handleEdit = () => {
    navigate(`/usuarios/editar/${id}`);
  };

  const handleBack = () => {
    navigate('/usuarios');
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <Typography>Carregando...</Typography>
      </Box>
    );
  }

  if (error || !usuario) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">Erro ao carregar os dados do usuário.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Cabeçalho */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography 
          variant="h4" 
          component="h1" 
          sx={{ 
            fontWeight: 'bold',
            fontFamily: 'Arial, sans-serif',
            fontSize: '2.125rem',
            lineHeight: 1.235
          }}
        >
          Visualizar Usuário
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={handleEdit}
          >
            Editar
          </Button>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
          >
            Voltar
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 3 }}>
        {/* Dados Principais */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main', mb: 2 }}>
            Dados Principais
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
            <Box>
              <Typography variant="body2" color="text.secondary">ID</Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{usuario.id}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Funcionário</Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                {usuario.nomeFuncionario || 'Não informado'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Nome de usuário</Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{usuario.usuario}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Perfil</Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                {usuario.role || 'Não informado'}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Dados de Sistema */}
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main', mb: 2 }}>
            Dados de Sistema
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
            <Box>
              <Typography variant="body2" color="text.secondary">ID Funcionário</Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                {usuario.idFuncionario || 'Não informado'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">ID Perfil de Acesso</Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                {usuario.idIdentityRole || 'Não informado'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">ID Role</Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                {usuario.idRole || 'Não informado'}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default UsuarioVisualizar;
